/**
 * Cross-coupon analytics dashboard — shows aggregate KPIs and usage trend
 * across ALL coupons for the current company.
 */

import { useCouponAnalytics } from "../api/promotionHooks";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function formatCurrency(value: number): string {
  return `฿${value.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

export function CouponAnalyticsDashboard() {
  const { data, isLoading } = useCouponAnalytics();

  if (isLoading) {
    return (
      <div className="mb-8 space-y-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-sm border bg-gray-50"
            />
          ))}
        </div>
        <div className="h-40 animate-pulse rounded-sm border bg-gray-50" />
      </div>
    );
  }

  if (!data) return null;

  const {
    total_issued,
    total_used,
    usage_rate,
    total_revenue,
    daily_trend,
    tier_breakdown,
  } = data;

  return (
    <div className="mb-8 space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KPICard
          label="Total Issued"
          value={total_issued.toLocaleString()}
          subtitle="coupon slots"
        />
        <KPICard
          label="Total Used"
          value={total_used.toLocaleString()}
          subtitle="redemptions"
        />
        <KPICard
          label="Usage Rate"
          value={`${usage_rate}%`}
          subtitle="used / issued"
        />
        <KPICard
          label="Total Revenue"
          value={formatCurrency(total_revenue)}
          subtitle="from coupon orders"
        />
      </div>

      {/* Usage Trend + Tier Breakdown */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Trend Chart */}
        <div className="rounded-sm border bg-white p-4 lg:col-span-2">
          <p className="mb-3 text-sm font-medium text-muted-foreground">
            Coupon Collections — Last 30 Days
          </p>
          {daily_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={daily_trend}>
                <defs>
                  <linearGradient
                    id="couponTrendGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor="hsl(var(--primary))"
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor="hsl(var(--primary))"
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  width={30}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip
                  labelFormatter={(label: string) => label}
                  formatter={(value) => [value, "Collections"]}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(var(--primary))"
                  fill="url(#couponTrendGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No collection data in the last 30 days
            </p>
          )}
        </div>

        {/* Tier Breakdown */}
        <div className="rounded-sm border bg-white p-4">
          <p className="mb-3 text-sm font-medium text-muted-foreground">
            By Customer Group
          </p>
          {tier_breakdown.length > 0 ? (
            <ul className="space-y-2">
              {tier_breakdown.slice(0, 8).map((tier) => (
                <li
                  key={tier.customer_group}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="truncate">{tier.customer_group}</span>
                  <span className="ml-2 font-medium tabular-nums">
                    {tier.count}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No tier data available
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function KPICard({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: string;
  subtitle: string;
}) {
  return (
    <div className="rounded-sm border bg-white p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
    </div>
  );
}
